import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useMedia } from 'react-use';
import { LazyLoadImage } from 'ck-ui';
import { getBrandImageUrl } from 'ck-utils';
import { getSanityImg } from 'src/sanityClient';
import { usePromotionView } from '../hooks';
import { getPromoName, trackPromotionClick } from '../utils';
import { ModalLink } from '../../ModalLink';
import styles from './FlexGrid.module.scss';

const FlexGrid = ({ isMultiBrand, isScrollable, zoomed, title, flexBlock, brands, handle, position, mobileColumns, desktopColumns }) => {
  const sliderRef = useRef(null);
  const isMobile = useMedia('(max-width: 767px)');

  useEffect(() => {
    if (isScrollable && sliderRef.current) {
      window.theme.initSlider(sliderRef.current);
    }
  }, [isScrollable, isMobile]);

  usePromotionView(
    brands && brands.length > 0
      ? brands.map((item, index) => ({
        id: handle,
        name: getPromoName(handle, item.trackingID || item.brand || item.href || `${title}-${position}-${index}`),
        creative: getBrandImageUrl(item.brand, 'centre'),
        position: `${handle}-${position}-${index}`,
      }))
      : flexBlock.map((item, index) => ({
        id: handle,
        name: getPromoName(handle, item.trackingID || item.imageTitle || item.url || `${title}-${position}-${index}`),
        creative: getSanityImg(item.image).width(600),
        position: `${handle}-${position}-${index}`,
      })),
    sliderRef
  );

  if (!flexBlock && !brands) return null;
  const desktopImgWidth = flexBlock.length > 3 ? 600 : 1080;

  return (
    <div className={classnames('container', { 'is-clipped': isScrollable, 'shop-brands': isMultiBrand })}>
      {(title) && (
        <div className="home-section-header">
          {title && <h2 className="heading5 is-uppercase">{title}</h2>}
        </div>
      )}

      <div
        ref={sliderRef}
        className={classnames(
          'columns',
          {
            'scrollable is-mobile': isScrollable,
            'product-list zoomed-carousel': isScrollable && zoomed,
            'is-variable is-2-mobile is-mobile is-multiline': isMultiBrand || mobileColumns || desktopColumns
          }
        )}
      >
        {flexBlock && flexBlock.map((item, index) => (
          <div
            key={item._key}
            className={classnames(
              'column',
              { 'product-tile-container': isScrollable && zoomed,
                'is-4-mobile': mobileColumns === '3',
                'is-half-mobile': mobileColumns === '2',
                'is-4-tablet': desktopColumns === '3',
                'is-half-tablet': desktopColumns === '2',
              })}
          >
            <a
              href={item.url}
              className="banner-link has-text-centered"
              data-modal-toggle={item.modal?.id}
              onClick={() => trackPromotionClick({
                id: handle,
                name: getPromoName(handle, item.trackingID || item.imageTitle || item.url || `${title}-${position}-${index}`),
                position: `${handle}-${position}-${index}`
              })}
            >
              <>
                <div
                  className="image-wrapper"
                  style={{ paddingBottom: `calc(${item.image.height / item.image.width} * 100%)` }}
                >
                  <LazyLoadImage
                    src={getSanityImg(item.image).width(isMobile ? 600 : desktopImgWidth).url()}
                    alt={item.imageTitle}
                  />
                </div>
                { item.imageTitle && (
                  <h3 className="heading7 mt-3 hover-underline">
                    {item.imageTitle}
                  </h3>
                )}
                { item.imageContent && (
                  <p className="caption-new">
                    {item.imageContent}
                  </p>
                )}
              </>
            </a>
            {item.modal?.id && (
              <ModalLink id={item.modal.id} title={item.modal.title} links={item.modal.links} description={item.modal.description} />
            )}
          </div>
        ))}
        {brands && brands.map((item, index) => (
          <div key={item._key} className={classnames('column', { 'is-4-mobile': isMultiBrand })}>
            <a
              href={item.href}
              className={styles.brand}
              onClick={() => trackPromotionClick({
                id: handle,
                name: getPromoName(handle, item.trackingID || item.brand || item.href || `${title}-${position}-${index}`),
                position: `${handle}-${position}-${index}`
              })}
            >
              <LazyLoadImage
                src={getBrandImageUrl(item.brand, 'centre')}
                alt={item.brand}
              />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

FlexGrid.propTypes = {
  flexBlock: PropTypes.array,
  isScrollable: PropTypes.bool,
  isMultiBrand: PropTypes.bool,
  title: PropTypes.string,
  brands: PropTypes.array,
  zoomed: PropTypes.bool,
  handle: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  mobileColumns: PropTypes.string,
  desktopColumns: PropTypes.string,
};

FlexGrid.defaultProps = {
  flexBlock: [],
  isScrollable: false,
  isMultiBrand: false,
  title: null,
  brands: null,
  zoomed: false,
  mobileColumns: null,
  desktopColumns: null
};

export { FlexGrid };
